// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Queue Page
// Main prescription queue with status filters
// ============================================================================

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Package,
  Truck,
  User,
  ChevronRight,
} from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { formatDistanceToNow } from 'date-fns';
import { Button, Badge, Spinner, Input, EmptyState, Card, CardContent } from '@/components/common/ui';
import { StatusBadge } from '@/components/common/StatusBadge';
import type { Prescription } from '@/types';

const QUEUE_FILTERS = [
  { value: '', label: 'All', icon: null, path: '/queue' },
  { value: 'PENDING_VERIFICATION', label: 'Verify', icon: User, path: '/queue/verification' },
  { value: 'PENDING_DUR', label: 'DUR Review', icon: AlertTriangle, path: '/queue/dur' },
  { value: 'PENDING_FILL', label: 'To Fill', icon: Package, path: '/queue/fill' },
  { value: 'PENDING_PHARMACIST_CHECK', label: 'Final Check', icon: CheckCircle2, path: '/queue/check' },
  { value: 'READY_FOR_PICKUP', label: 'Ready', icon: Truck, path: '/queue/ready' },
];

// Map URL paths to status filters
const PATH_TO_STATUS: Record<string, string> = {
  '/queue': '',
  '/queue/verification': 'PENDING_VERIFICATION',
  '/queue/dur': 'PENDING_DUR',
  '/queue/fill': 'PENDING_FILL',
  '/queue/check': 'PENDING_PHARMACIST_CHECK',
  '/queue/ready': 'READY_FOR_PICKUP',
};

export function QueuePage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Derive initial filter from URL path
  const initialFilter = PATH_TO_STATUS[location.pathname] ?? '';
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');

  // Update filter when URL changes
  useEffect(() => {
    const newFilter = PATH_TO_STATUS[location.pathname] ?? '';
    setFilter(newFilter);
  }, [location.pathname]);

  // Handle filter change - update URL
  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    const filterConfig = QUEUE_FILTERS.find(f => f.value === newFilter);
    if (filterConfig) {
      navigate({ to: filterConfig.path as any });
    }
  };

  // Fetch queue with auto-refresh
  const {
    data: queue,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['queue', filter],
    queryFn: () => queueApi.getQueue({ status: filter || undefined }),
    refetchInterval: 10000,
  });

  // Fetch queue counts
  const { data: counts } = useQuery({
    queryKey: ['queue', 'counts'],
    queryFn: () => queueApi.getQueueCounts(),
    refetchInterval: 10000,
  });

  // Map status to count keys
  const statusToCountKey: Record<string, keyof NonNullable<typeof counts>> = {
    'PENDING_VERIFICATION': 'pendingVerification',
    'PENDING_DUR': 'pendingDUR',
    'PENDING_FILL': 'pendingFill',
    'PENDING_PHARMACIST_CHECK': 'pendingCheck',
    'READY_FOR_PICKUP': 'ready',
  };

  // Update filter counts
  const filtersWithCounts = QUEUE_FILTERS.map((f) => ({
    ...f,
    count: f.value && counts ? counts[statusToCountKey[f.value]] || 0 : counts?.total || 0,
  }));

  // Filter by search
  const filteredQueue = queue?.data?.filter(
    (rx: Prescription) =>
      rx.rxNumber.toLowerCase().includes(search.toLowerCase()) ||
      rx.patientName.toLowerCase().includes(search.toLowerCase()) ||
      rx.drugName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Prescription Queue</h1>
          <p className="text-slate-500">
            {queue?.data?.length || 0} prescriptions • Last updated{' '}
            {formatDistanceToNow(new Date())} ago
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filtersWithCounts.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'primary' : 'outline'}
            size="sm"
            onClick={() => handleFilterChange(f.value)}
            className="relative"
          >
            {f.icon && <f.icon className="h-4 w-4 mr-1" />}
            {f.label}
            {f.count > 0 && (
              <Badge
                variant={filter === f.value ? 'secondary' : 'primary'}
                className="ml-2"
              >
                {f.count}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Search */}
      <Input
        placeholder="Search by Rx#, patient name, or drug..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      {/* Queue List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !filteredQueue?.length ? (
        <EmptyState
          icon={<CheckCircle2 className="h-12 w-12" />}
          title="Queue is empty"
          description={
            filter ? 'No prescriptions match this filter.' : 'All caught up!'
          }
        />
      ) : (
        <div className="space-y-3">
          {filteredQueue.map((rx: Prescription) => (
            <QueueItem key={rx.id} rx={rx} />
          ))}
        </div>
      )}
    </div>
  );
}

function QueueItem({ rx }: { rx: Prescription }) {
  const urgency = getUrgency(rx);
  const receivedAt = rx.receivedAt || rx.createdAt;

  // Determine the correct route based on status
  const getRouteForStatus = (status: string): string => {
    switch (status) {
      case 'PENDING_VERIFICATION':
        return `/rx/${rx.id}/verify` as const;
      case 'PENDING_DUR':
        return `/rx/${rx.id}/dur` as const;
      case 'PENDING_FILL':
        return `/rx/${rx.id}/fill` as const;
      case 'PENDING_PHARMACIST_CHECK':
        return `/rx/${rx.id}/check` as const;
      default:
        return `/rx/${rx.id}/verify` as const;
    }
  };

  return (
    <Link to={getRouteForStatus(rx.status) as any}>
      <Card
        className={`hover:border-brand-300 transition-colors cursor-pointer ${urgency.borderClass}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono font-medium text-slate-900">
                  Rx #{rx.rxNumber}
                </span>
                <StatusBadge status={rx.status} />
                {rx.hasDurAlerts && (
                  <Badge variant="warning">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    DUR Alert
                  </Badge>
                )}
                {rx.isControlled && (
                  <Badge variant="danger">C-{rx.deaSchedule}</Badge>
                )}
                {urgency.badge}
              </div>

              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Patient:</span>{' '}
                  <span className="font-medium">{rx.patientName}</span>
                </div>
                <div>
                  <span className="text-slate-500">Drug:</span>{' '}
                  <span className="font-medium">
                    {rx.drugName} {rx.strength}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Qty:</span>{' '}
                  <span className="font-medium">{rx.quantity}</span>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
                <span>
                  <Clock className="h-3 w-3 inline mr-1" />
                  Received {formatDistanceToNow(new Date(receivedAt))} ago
                </span>
                <span>Dr. {rx.prescriberName}</span>
              </div>
            </div>

            <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function getUrgency(rx: Prescription) {
  const receivedAt = rx.receivedAt || rx.createdAt;
  const ageMinutes = (Date.now() - new Date(receivedAt).getTime()) / 60000;

  if (rx.isStatOrder || ageMinutes > 120) {
    return {
      borderClass: 'border-l-4 border-l-red-500',
      badge: <Badge variant="danger">URGENT</Badge>,
    };
  }

  if (ageMinutes > 60) {
    return {
      borderClass: 'border-l-4 border-l-amber-500',
      badge: <Badge variant="warning">PRIORITY</Badge>,
    };
  }

  return { borderClass: '', badge: null };
}
